import { Button, Card, DatePicker, Form, Image, Input, InputNumber, message, Radio, Space, Switch, Table, Tag} from 'antd';
import type { Dayjs } from 'dayjs';
import * as Component from 'components';
import React, { useCallback, useMemo, useState } from 'react';
import { contentRelayService } from '../service/contentRelayService';
import { ContentRelayResponse, SeasonContent } from '../type/contentRelayType';

const ContentRelay: React.FC = () => {
  const [form] = Form.useForm();
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [seasonTitle, setSeasonTitle] = useState<string>('');
  const [seasonTitleEn, setSeasonTitleEn] = useState<string>('');
  const [contents, setContents] = useState<SeasonContent[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [thumbnailContentId, setThumbnailContentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ContentRelayResponse | null>(null);

  const generateTitles = useCallback((date: Dayjs | null) => {
    if (!date) return;
    const dateStr = date.format('MM/DD/YYYY');
    const episodeOrder = Number(date.format('MMDDYYYY'));
    form.setFieldsValue({
      title: seasonTitle ? `${seasonTitle} : ${dateStr}` : dateStr,
      title_en: seasonTitleEn ? `${seasonTitleEn} : ${dateStr}` : dateStr,
      episode_order: episodeOrder,
    });
  }, [form, seasonTitle, seasonTitleEn]);

  const handleLoadContents = async () => {
    if (!seasonId) {
      message.warning('Please enter Season ID');
      return;
    }
    setLoading(true);
    try {
      const data = await contentRelayService.fetchSeasonContents(seasonId, 1, 50);
      setContents(data.contents);
      setSeasonTitle(data.season_title || '');
      setSeasonTitleEn(data.season_title_en || data.season_title || '');
      setSelectedIds([]);
      setThumbnailContentId(null);
      setResult(null);
      form.resetFields();
    } catch (e: any) {
      console.error('Failed to load episodes:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRelay = async () => {
    if (selectedIds.length < 2) {
      message.warning('Please select at least 2 episodes');
      return;
    }
    if (!seasonId) return;

    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const response = await contentRelayService.relay({
        season_id: seasonId,
        source_content_ids: selectedIds,
        title: values.title || undefined,
        title_en: values.title_en || undefined,
        episode_order: values.episode_order || undefined,
        on_air_date: values.on_air_date?.format('YYYY-MM-DD') || undefined,
        skip_mp4_merge: values.skip_mp4_merge || false,
        thumbnail_content_id: thumbnailContentId || undefined,
      });
      setResult(response);
      message.success(`Merge completed! Content ID: ${response.id}`);
    } catch (e: any) {
      console.error('Merge failed:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedContents = useMemo(
    () => selectedIds
      .map(id => contents.find(c => c.id === id))
      .filter((c): c is SeasonContent => c !== undefined),
    [contents, selectedIds]
  );

  const totalDuration = useMemo(
    () => selectedContents.reduce((sum, c) => sum + (c.duration || 0), 0),
    [selectedContents]
  );

  const columns = [
    {
      title: '#',
      key: 'order',
      width: 50,
      render: (_: any, record: SeasonContent) => {
        const idx = selectedIds.indexOf(record.id);
        return idx >= 0 ? (
          <Tag color="blue" style={{ margin: 0 }}>{idx + 1}</Tag>
        ) : null;
      },
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Part',
      dataIndex: 'episode_part',
      key: 'episode_part',
      width: 60,
      render: (part: number | null) => part ?? '-',
    },
    {
      title: 'Order',
      dataIndex: 'episode_order',
      key: 'episode_order',
      width: 110,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (d: number) => d ? `${Math.floor(d / 60)}m${d % 60}s` : '-',
    },
    {
      title: 'Source',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      width: 100,
      render: (src: string) => (
        <Tag color={src === 'in-house' ? 'blue' : 'green'}>{src}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys: React.Key[]) => {
      // Maintain selection order: only append newly added keys at the end
      const newKeys = (keys as number[]).filter(k => !selectedIds.includes(k));
      const removed = selectedIds.filter(k => !keys.includes(k));
      const updated = selectedIds.filter(k => !removed.includes(k)).concat(newKeys);
      setSelectedIds(updated);
      if (thumbnailContentId && !keys.includes(thumbnailContentId)) {
        setThumbnailContentId(null);
      }
    },
  };

  return (
    <Component.BasePageComponent>
      <h2>Content Relay (Merge Episodes)</h2>

      <Card title="1. Select Season" style={{ marginBottom: 16 }}>
        <Space>
          <InputNumber
            placeholder="Season ID"
            value={seasonId}
            onChange={(v) => setSeasonId(v)}
            style={{ width: 200 }}
          />
          <Button type="primary" onClick={handleLoadContents} loading={loading}>
            Load Episodes
          </Button>
          {seasonTitle && (
            <span style={{ color: '#666' }}>{seasonTitle}</span>
          )}
        </Space>
      </Card>

      {contents.length > 0 && (
        <Card
          title={`2. Select Episodes to Merge (${selectedIds.length} selected${totalDuration ? `, Total ${Math.floor(totalDuration / 60)}m${totalDuration % 60}s` : ''})`}
          style={{ marginBottom: 16 }}
        >
          <Table
            rowKey="id"
            columns={columns}
            dataSource={contents}
            rowSelection={rowSelection}
            pagination={{ pageSize: 20 }}
            size="small"
          />
        </Card>
      )}

      {selectedIds.length >= 2 && (
        <>
          <Card title="3. Select Representative Thumbnail" style={{ marginBottom: 16 }}>
            <Radio.Group
              value={thumbnailContentId}
              onChange={(e) => setThumbnailContentId(e.target.value)}
            >
              <Space direction="vertical" size="middle">
                {selectedContents.map(c => (
                  <Radio key={c.id} value={c.id} style={{ display: 'flex', alignItems: 'center' }}>
                    <Space>
                      {c.poster?.url ? (
                        <Image
                          src={c.poster?.url}
                          width={120}
                          height={68}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                          preview={false}
                        />
                      ) : (
                        <div style={{
                          width: 120, height: 68, background: '#f0f0f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: 4, fontSize: 12, color: '#999'
                        }}>
                          No Image
                        </div>
                      )}
                      <span>{c.title}</span>
                    </Space>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Card>

          <Card title="4. Merge Settings" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" style={{ maxWidth: 500 }}>
              <Form.Item name="on_air_date" label="On Air Date" rules={[{ required: true, message: 'Please select an On Air Date' }]}>
                <DatePicker
                  style={{ width: '100%' }}
                  onChange={(date) => generateTitles(date)}
                />
              </Form.Item>
              <Form.Item name="title" label="Korean Title">
                <Input readOnly style={{ background: '#f5f5f5' }} />
              </Form.Item>
              <Form.Item name="title_en" label="English Title">
                <Input readOnly style={{ background: '#f5f5f5' }} />
              </Form.Item>
              <Form.Item name="episode_order" label="Episode Order">
                <InputNumber placeholder="e.g. 3272026" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="skip_mp4_merge"
                label="Skip MP4 Merge"
                valuePropName="checked"
              >
                <Switch checkedChildren="Skip" unCheckedChildren="Merge" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleRelay}
                  loading={submitting}
                  danger
                >
                  Execute Merge ({selectedIds.length} episodes)
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </>
      )}

      {result && (
        <Card title="Result" style={{ marginBottom: 16 }}>
          <p><strong>Content ID:</strong> {result.id}</p>
          <p><strong>Title:</strong> {result.title}</p>
          <p><strong>Duration:</strong> {result.duration}s</p>
          <p><strong>Status:</strong> {result.status}</p>
          <p>
            <a
              href={`/content/edit/${result.id}`}
              target="_blank"
              rel="noreferrer"
            >
              Go to Content Edit Page
            </a>
          </p>
        </Card>
      )}
    </Component.BasePageComponent>
  );
};

export default ContentRelay;
